import { Alert, Box, Snackbar, Typography } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { FormProvider } from "react-hook-form";
import { FoilCutterForm } from "../../components/common/form/FoilCutterForm";
import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import {
  fetchAllFoilTypes,
  fetchLastMaterialFoil,
} from "../../services/materialFoilService";
import { LoaderFunctionArgs, useLoaderData } from "react-router-dom";
import { FoilCutterType, FoilType } from "../../types/foilCutterTypes";
import { MaterialFoilType } from "../../types/materialFoilTypes";
import { useFoilCutterForm } from "../../hooks/useFoilCutterForm";

export async function loader(_: LoaderFunctionArgs) {
  const stationId = 2;
  const [foilType, materialFoil] = await Promise.all([
    fetchAllFoilTypes(stationId),
    fetchLastMaterialFoil(stationId),
  ]);
  return {
    title: "Foil Cutter 1",
    foilType,
    materialFoil,
    stationId,
  };
}

export default function FoilCutterOnePage() {
  const methods = useFoilCutterForm();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loaderData = useLoaderData() as {
    foilType: FoilType[];
    materialFoil: FoilCutterType[];
    stationId: number;
  };

  const [rows, setRows] = useState<FoilCutterType[]>(loaderData.materialFoil);

  const refreshRows = useCallback(async () => {
    try {
      const refreshed = await fetchLastMaterialFoil(loaderData.stationId);
      setRows(refreshed as unknown as FoilCutterType[]);
    } catch (e) {}
  }, [loaderData.stationId]);

  const handleSuccess = () => {
    setShowSuccess(true);
    refreshRows();
  };

  const handleSaved = (item: MaterialFoilType) => {
    const mapped: FoilCutterType = {
      ...(item as any),
    } as FoilCutterType;
    setRows((prev) => [mapped, ...prev].slice(0, 15));
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setShowSnackbar(false);
  };

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <FoilCutterForm
              onSuccess={handleSuccess}
              onError={handleError}
              onSaved={handleSaved}
              foilTypes={loaderData.foilType}
              stationId={loaderData.stationId}
            />
          </FormProvider>
        </Box>

        {/* Table */}
        <Box flex={3}>
          <Typography variant="h6" gutterBottom>
            Zuletzt verwendete Folien
          </Typography>
          <DynamicTable columns={columns} data={rows} />
        </Box>
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Folie erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "Ein Fehler ist aufgetreten."}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const isExpired = (value: unknown) => {
  if (!value) return false;
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

const columns: TableColumn<FoilCutterType>[] = [
  {
    key: "dateInProduction",
    label: "Produktionsdatum",
    render: (value, _row) =>
      value ? format(new Date(value as string), "yyyy-MM-dd HH:mm:ss") : "-",
  },
  {
    key: "firstname",
    label: "Operator",
    render: (_value, row) =>
      [(row as any)?.firstname, (row as any)?.lastname]
        .filter(Boolean)
        .join(" ") || "-",
  },
  {
    key: "foilBatch",
    label: "Batch Nummer",
    render: (value, _row) => (value ? String(value) : "-"),
  },
  {
    key: "foilExpirationDate",
    label: "Ablaufdatum",
    render: (value) => {
      if (!value) return "-";
      const dateStr = format(new Date(value as string), "yyyy-MM-dd");
      const expired = isExpired(value);
      return (
        <Typography component="span" color={expired ? "error.main" : "inherit"}>
          {dateStr}
        </Typography>
      );
    },
  },
  {
    key: "isFoilSampleStored",
    label: "Rückstellmuster",
    render: (value) => (value ? "Ja" : "Nein"),
  },
  {
    key: "name",
    label: "Folienname",
    render: (value, _row) => (value ? String(value) : "-"),
  },
];
